"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function RedefiningYourDrive() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222453" id="coastal-sanctuary-hero" className="relative min-h-[90vh] w-full overflow-hidden bg-background flex items-center py-24 lg:py-32">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[15rem] md:text-[25rem] lg:text-[35rem] font-default font-bold text-primary/5 select-none pointer-events-none z-0 tracking-tighter leading-none">
        CC
      </div>

      {}
      <div className="absolute top-1/4 right-1/4 w-[50vw] h-[50vw] bg-accent/10 blur-[120px] rounded-full pointer-events-none z-0" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h1 className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-default font-bold leading-[0.9] tracking-tighter text-foreground uppercase">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent pb-2">
                Clean Home.
              </span>
              Happy Life.
            </h1>
            
            <p className="font-sans text-muted-foreground text-base md:text-lg max-w-xl leading-relaxed border-l-2 border-accent/50 pl-4">Vero Beach's premier eco-friendly cleaning service. Experience a professional, sparkling touch that brings the serenity of the coast into your living space with our trusted care. Book now.</p>

            <div className="pt-4">
              <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 text-base md:text-lg px-8 py-7 uppercase tracking-widest font-default shadow-lg transition-all active:translate-x-1 active:translate-y-1 active:shadow-none">
                <Link to="#">
                  Call to Book Now <ArrowRight className="ml-3 size-5" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative mt-12 lg:mt-0 transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative w-full aspect-square md:aspect-video lg:aspect-square flex items-center justify-center">
              {}
              <div className="relative w-full h-full lg:w-[130%] lg:-ml-[15%] flex items-center justify-center transform lg:-rotate-2 hover:rotate-0 transition-transform duration-700">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/luxury_sedan_3d_angled.png" alt="Sun-drenched Vero Beach living room interior" className="w-full h-auto object-contain drop-shadow-2xl filter brightness-110" loading="eager" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}